import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  Wallet, 
  TrendingUp, 
  History, 
  Settings, 
  Copy,
  CheckCircle,
  AlertCircle,
  Loader,
  ArrowUpRight,
  ArrowDownLeft,
  Clock
} from 'lucide-react';
import API_ENDPOINTS from '../../config/api';

const MemberWallet = () => {
  const [walletData, setWalletData] = useState({
    wallet_address: '',
    total_earnings: 0,
    matrix_positions: 0
  });
  
  const [payments, setPayments] = useState([]);
  const [newWalletAddress, setNewWalletAddress] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);

  useEffect(() => {
    loadWalletData();
    loadPaymentHistory();
  }, []);

  const loadWalletData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/member/wallet`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWalletData(data);
        setNewWalletAddress(data.wallet_address || '');
        setShowUpdateForm(!data.wallet_address);
      } else {
        toast.error('Failed to load wallet information');
      }
    } catch (error) {
      console.error('Error loading wallet:', error);
      toast.error('Error loading wallet information');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentHistory = async () => {
    setPaymentsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user'));
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/payments/member/${user.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPayments(data.payments || []);
      } else {
        console.error('Failed to load payment history');
      }
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const updateWalletAddress = async () => {
    if (!newWalletAddress) {
      toast.error('Please enter a wallet address');
      return;
    }

    // Validate Tron wallet address
    if (!newWalletAddress.startsWith('T') || newWalletAddress.length !== 34) {
      toast.error('Invalid Tron wallet address format');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/member/wallet`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ wallet_address: newWalletAddress })
      });

      if (response.ok) {
        toast.success('Wallet address updated successfully');
        await loadWalletData();
        setShowUpdateForm(false);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update wallet address');
      }
    } catch (error) {
      console.error('Error updating wallet:', error);
      toast.error('Error updating wallet address');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copied to clipboard');
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'finished':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'waiting':
      case 'confirming':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'failed':
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'finished':
        return 'text-green-600 bg-green-100';
      case 'waiting':
      case 'confirming':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">My Wallet</h1>
          <p className="text-gray-600 mt-2">
            Manage your receiving wallet and track earnings
          </p>
        </div>
        
        {walletData.wallet_address && (
          <Button onClick={() => setShowUpdateForm(!showUpdateForm)} variant="outline">
            <Settings className="w-4 h-4 mr-2" />
            Update Wallet
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${walletData.total_earnings.toFixed(2)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100">
                <Wallet className="w-6 h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Matrix Positions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {walletData.matrix_positions}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100">
                <History className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Transactions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {payments.length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Wallet Address Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Wallet className="w-5 h-5 mr-2" />
            Receiving Wallet Address
          </CardTitle>
          <CardDescription>
            This is where your payments will be sent directly. Payments are non-custodial - 
            BBC never holds your funds.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {walletData.wallet_address ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">Current Wallet Address:</p>
                  <p className="font-mono text-sm break-all">{walletData.wallet_address}</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(walletData.wallet_address)}
                  className="ml-4"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              {showUpdateForm && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Wallet Address
                    </label>
                    <input
                      type="text"
                      value={newWalletAddress}
                      onChange={(e) => setNewWalletAddress(e.target.value)}
                      placeholder="Enter new Tron wallet address (starts with T)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="flex space-x-3">
                    <Button onClick={updateWalletAddress} disabled={saving}>
                      {saving ? <Loader className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                      Update Address
                    </Button>
                    <Button variant="outline" onClick={() => setShowUpdateForm(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Wallet Address Required</h4>
                    <p className="text-sm text-yellow-700 mt-1">
                      Please set your Tron wallet address to receive payments.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tron Wallet Address *
                  </label>
                  <input
                    type="text"
                    value={newWalletAddress}
                    onChange={(e) => setNewWalletAddress(e.target.value)}
                    placeholder="Enter your Tron wallet address (starts with T, 34 characters)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Example: TRon7xkVrr4dPvQ9zG8vJfJpNVqEQw7nKq
                  </p>
                </div>
                <Button onClick={updateWalletAddress} disabled={saving}>
                  {saving ? <Loader className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Set Wallet Address
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <History className="w-5 h-5 mr-2" />
            Payment History
          </CardTitle>
          <CardDescription>
            Your recent payment transactions
          </CardDescription>
        </CardHeader>
        <CardContent>
          {paymentsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8">
              <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No payment history yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {payments.map((payment, index) => (
                <div key={payment.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center space-x-4">
                    <div className={`p-2 rounded-full ${payment.type === 'received' ? 'bg-green-100' : 'bg-blue-100'}`}>
                      {payment.type === 'received' ? (
                        <ArrowDownLeft className="w-4 h-4 text-green-600" />
                      ) : (
                        <ArrowUpRight className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{payment.product_name}</p>
                      <p className="text-sm text-gray-500">
                        {payment.type === 'received' ? 'Received' : 'Sent'} • {formatDate(payment.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">
                      {payment.type === 'received' ? '+' : '-'}${payment.amount.toFixed(2)}
                    </p>
                    <div className="flex items-center justify-end mt-1">
                      {getStatusIcon(payment.status)}
                      <span className={`text-xs px-2 py-1 rounded-full ml-2 ${getStatusColor(payment.status)}`}>
                        {payment.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MemberWallet;